<template>
  <div>
    <SidebarSettingsGroup
      title="Fox Foresight Chart"
      collapsible
      :element-type="element.type"
      data-test="graphics-foxforesightSettings-general"
    >
      <div class="sidebar-form-group">
        <label class="sidebar-form-label">
          Chart Type
        </label>
        <select
          v-model="chartType"
          class="form-control"
          data-test="graphics-foxforesightSettings-chartType"
          data-entity="graphics-foxforesightSettings-chartType"
          @change="onChartTypeChange"
        >
          <option
            v-for="chart in availableCharts"
            :key="chart.value"
            :value="chart.value"
          >
            {{ chart.label }}
          </option>
        </select>
      </div>

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">
          Date
        </label>
        <div class="datepicker">
          <date-picker
            v-model:value="selectedDate"
            type="date"
            format="YYYY-MM-DD"
            value-type="format"
            :clearable="false"
            placeholder="Select date"
            data-test="graphics-foxforesightSettings-date"
            data-entity="graphics-foxforesightSettings-date"
            @change="onDateChange"
          />
        </div>
      </div>

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">
          Game
        </label>
        <select
          v-model="selectedGame"
          class="form-control"
          data-test="graphics-foxforesightSettings-game"
          data-entity="graphics-foxforesightSettings-game"
          :disabled="loadingGames"
          @change="onGameChange"
        >
          <option value="">
            {{ loadingGames ? 'Loading games...' : 'Select a game' }}
          </option>
          <option
            v-for="game in availableGames"
            :key="game.id"
            :value="game.id"
          >
            {{ game.name }}
          </option>
        </select>
      </div>

      <!-- Dynamic parameters based on chart type and game -->
      <template v-if="chartParameters.length > 0">
        <div
          v-for="param in chartParameters"
          :key="param.key"
          class="sidebar-form-group"
        >
          <label class="sidebar-form-label">
            {{ param.label }}
            <span
              v-if="param.required"
              class="text-danger"
            >*</span>
          </label>

          <!-- Single select (treating multi-select as single for API compatibility) -->
          <select
            v-if="param.type === 'select' || param.type === 'multi-select'"
            :value="getParameterValue(param.key)"
            class="form-control"
            :data-test="`graphics-foxforesightSettings-${param.key}`"
            :data-entity="`graphics-foxforesightSettings-${param.key}`"
            @input="updateParameter(param.key, $event.target.value)"
          >
            <option value="">
              {{ param.required ? 'Select...' : 'All' }}
            </option>
            <option
              v-for="option in param.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>

          <!-- Text input -->
          <input
            v-else
            :value="getParameterValue(param.key)"
            type="text"
            class="form-control"
            :placeholder="param.label"
            :data-test="`graphics-foxforesightSettings-${param.key}`"
            :data-entity="`graphics-foxforesightSettings-${param.key}`"
            @input="updateParameter(param.key, $event.target.value)"
          >
        </div>
      </template>

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Transparent Background
          <input
            v-model="transparent"
            type="checkbox"
            data-test="graphics-foxforesightSettings-toggleTransparent"
            data-entity="graphics-foxforesightSettings-toggleTransparent"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-foxforesightSettings-styleAndAnimation"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-foxforesightSettings-toggleBorder"
            data-entity="graphics-foxforesightSettings-toggleBorder"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <template v-if="border">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <validation-field as="input"
                v-model.number="borderWidth"
                rules="required|numeric|min:0"
                type="number"
                name="borderWidth"
                min="0"
                :class="{ 'is-invalid': validationErrors['borderWidth'] }"
                data-test="graphics-foxforesightSettings-setBorderWidth"
                data-entity="graphics-foxforesightSettings-setBorderWidth"
              />
              <label class="sidebar-input-sub-label">Width</label>
            </div>
            <div class="settings-col-6 settings-col-xs colorpicker">
              <colorpicker
                v-model="borderColor"
                :is-mapped="checkMapped('borderColor')"
              />
              <label class="sidebar-input-sub-label">
                Color
                <span
                  data-balloon="This setting is controlled by data. Unmap data to customize."
                  data-balloon-pos="down-right"
                  data-balloon-length="medium"
                >
                  <span
                    v-if="checkMapped('borderColor')"
                    class="fa fa-lock mapped-lock"
                  />
                </span>
              </label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <box-shadow-settings
        :element="element"
        :data-provider="dataProvider"
        :scroll-offset="scrollOffset"
      />

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Blur
          <input
            v-model="blur"
            type="checkbox"
            data-test="graphics-foxforesightSettings-toggleBlur"
            data-entity="graphics-foxforesightSettings-toggleBlur"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="blur"
          class="input-group"
          data-test="graphics-foxforesightSettings-setBlurSize"
        >
          <validation-field as="input"
            v-model.number="blurSize"
            rules="required|numeric|min:0"
            type="number"
            name="blurSize"
            min="0"
            :class="{ 'is-invalid': validationErrors['blurSize'] }"
            data-test="graphics-foxforesightSettings-setBlurSize"
            data-entity="graphics-foxforesightSettings-setBlurSize"
          />
          <span class="input-group-addon">px</span>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <animation-settings :element="element" />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import datePicker from 'vue-datepicker-next';
import SettingsMixins from '../../mixins/settings-mixins';
import Partners from '../../services/partners';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';

export default {
  name: 'FoxForesightSettingsComponent',

  components: {
    AnimationSettings,
    OpacitySettings,
    BoxShadowSettings,
    datePicker,
  },

  mixins: [SettingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },

    scrollOffset: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      availableCharts: [],
      availableGames: [],
      loadingGames: false,
      loadingParameters: false,
      chartParameters: [],
      updateUrlDebounce: null,
      selectedChartSport: '',
    };
  },

  computed: {
    chartType: {
      get() {
        return this.getPropertyValue('chartType');
      },

      set(v) {
        this.updateOneElementValue('chartType', v);
        this.updateUrl();
      },
    },

    selectedDate: {
      get() {
        return this.getPropertyValue('date');
      },

      set(v) {
        this.updateOneElementValue('date', v);
        this.updateUrl();
      },
    },

    selectedGame: {
      get() {
        return this.getPropertyValue('game');
      },

      set(v) {
        this.updateOneElementValue('game', v);
        this.updateUrl();
      },
    },

    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },

      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },

    border: {
      get() {
        return this.getPropertyValue('border');
      },

      set(v) {
        this.updateOneElementValue('border', v);
      },
    },

    transparent: {
      get() {
        return this.getPropertyValue('transparent') || false;
      },

      set(v) {
        this.updateOneElementValue('transparent', v);
        this.updateUrl();
      },
    },

    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },

      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },

    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { mappedPath: 'borderColor' });
      },

      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },

    blur: {
      get() {
        return this.getPropertyValue('blur');
      },

      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },

    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },

      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },

  watch: {
    'element.styles.width': {
      handler() {
        this.debouncedUpdateUrl();
      },
    },
    'element.styles.height': {
      handler() {
        this.debouncedUpdateUrl();
      },
    },
  },

  async mounted() {
    await this.loadCharts();
    if (this.selectedDate) {
      await this.loadGames();
    }
    if (this.chartType && this.selectedGame) {
      await this.loadChartParameters();
    }
  },

  beforeUnmount() {
    if (this.updateUrlDebounce) {
      clearTimeout(this.updateUrlDebounce);
    }
  },

  methods: {
    debouncedUpdateUrl() {
      if (this.updateUrlDebounce) {
        clearTimeout(this.updateUrlDebounce);
      }
      this.updateUrlDebounce = setTimeout(() => {
        this.updateUrl();
      }, 300);
    },

    async loadCharts() {
      try {
        const charts = await Partners.getFoxForesightCharts();
        // API returns array directly, map to format used by dropdown
        this.availableCharts = (charts || []).map((chart) => ({
          value: chart.chart_type,
          label: chart.label,
          sport: chart.sport,
        }));
        
        // Set sport for current chart type if it exists
        if (this.chartType) {
          const currentChart = this.availableCharts.find((chart) => chart.value === this.chartType);
          this.selectedChartSport = currentChart?.sport || '';
        }
      } catch (error) {
        console.error('Failed to load charts:', error);
      }
    },

    async loadGames() {
      if (!this.selectedDate) return;

      this.loadingGames = true;
      try {
        const games = await Partners.getFoxForesightGames(this.selectedDate, this.selectedChartSport);
        // API returns array directly, map to format used by dropdown
        this.availableGames = (games || []).map((game) => ({
          id: game.game_id,
          name: game.label,
        }));
      } catch (error) {
        console.error('Failed to load games:', error);
      } finally {
        this.loadingGames = false;
      }
    },

    async loadChartParameters() {
      if (!this.chartType || !this.selectedGame) return;

      this.loadingParameters = true;
      try {
        const data = await Partners.getFoxForesightChartOptions(this.chartType, this.selectedGame);
        this.chartParameters = data.parameters || [];

        // Set defaults for new parameters that don't have values yet
        this.chartParameters.forEach((param) => {
          const existingValue = this.getPropertyValue(param.key);

          // Only set default if no existing value
          if (!existingValue) {
            if (param.type === 'select' || param.type === 'multi-select') {
              // For player_id, hit_result, hit_direction, and pitcher_hand,
              // always default to empty/"All" option
              const alwaysEmptyFields = ['player_id', 'hit_result', 'hit_direction', 'pitcher_hand'];
              if (alwaysEmptyFields.includes(param.key)) {
                this.updateOneElementValue(param.key, '');
              } else {
                // For other fields (like view), prefer first option
                const emptyOption = param.options.find((opt) => opt.value === '');
                const defaultValue = emptyOption ? '' : (param.options?.[0]?.value ?? '');
                this.updateOneElementValue(param.key, defaultValue);
              }
            }
          }
        });
      } catch (error) {
        console.error('Failed to load chart parameters:', error);
        this.chartParameters = [];
      } finally {
        this.loadingParameters = false;
      }
    },

    getParameterValue(key) {
      return this.getPropertyValue(key) || '';
    },

    updateParameter(key, value) {
      this.updateOneElementValue(key, value);
      this.updateUrl();
    },

    onChartTypeChange() {
      // Find the selected chart and get its sport
      const selectedChart = this.availableCharts.find((chart) => chart.value === this.chartType);
      this.selectedChartSport = selectedChart?.sport || '';
      
      this.selectedGame = '';
      this.availableGames = [];
      this.chartParameters = [];
      // Don't reset date - keep the current date
      // Load games if date is already set
      if (this.selectedDate) {
        this.loadGames();
      }
      this.updateUrl();
    },

    onDateChange() {
      this.selectedGame = '';
      this.chartParameters = [];
      this.loadGames();
      this.updateUrl();
    },

    onGameChange() {
      this.chartParameters = [];
      if (this.selectedGame) {
        this.loadChartParameters();
      }
      this.updateUrl();
    },

    updateUrl() {
      const chartType = this.getPropertyValue('chartType');
      const date = this.getPropertyValue('date');
      const game = this.getPropertyValue('game');

      if (!chartType || !date || !game) {
        this.updateOneElementValue('imageUrl', '');
        return;
      }

      const params = new URLSearchParams();

      // Add required parameters with API-expected names
      params.append('chart_type', chartType);
      params.append('game_id', game);

      // Add width and height for proper image scaling
      if (this.element.styles?.width) {
        params.append('width', Math.round(this.element.styles.width));
      }
      if (this.element.styles?.height) {
        params.append('height', Math.round(this.element.styles.height));
      }

      // Add transparent parameter if enabled
      if (this.getPropertyValue('transparent')) {
        params.append('transparent', 'true');
      }

      // Add all other parameters from chart parameters
      this.chartParameters.forEach((param) => {
        const value = this.getPropertyValue(param.key);
        if (value) {
          params.append(param.key, value);
        }
      });

      const url = `${process.env.MONO_BASE}/partners/sportsgold/render?${params.toString()}`;
      this.updateOneElementValue('imageUrl', url);
    },
  },
};
</script>
